import api from "@/libs/axios";
import { QueryConfig } from "@/libs/query";
import { useQuery } from "@tanstack/react-query";
import { apiEnum } from "@/pages/taskpane/shared/types/enums/apiEnum";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";
import { GetSelectedResourceResponse, IGetResourceBookingRequest } from "./types/resourceEntity";

/**
 * Fetch selected resource data
 * Can be used for imperative calls outside of React components
 */
export const getSelectedResource = async (
  payload: IGetResourceBookingRequest
): Promise<GetSelectedResourceResponse> => {
  try {
    const { data } = await api.post(apiEnum.getSelectedResource, payload);
    return new GetSelectedResourceResponse(data?.data || data);
  } catch (error) {
    return new GetSelectedResourceResponse();
  }
};

/**
 * Query options for selected resource
 */
export const options = (payload: IGetResourceBookingRequest) => ({
  queryKey: [
    QUERY_KEY.GET_RESOURCES,
    QUERY_KEY.GET_SELECTED_RESOURCE,
    {
      //requestId: payload.requestId,
      organizer: payload.organizer,
      uid: payload.uid,
      globalId: payload.globalId,
      timeZone: payload.timeZoneStandardName,
      isNoEndDate: payload.isNoEndDate,
      isRecurring: payload.isRecurring,
      occurrencesHash: JSON.stringify(payload.occurrences?.map(o => ({
        start: o.utcStartDate,
        end: o.utcEndDate
      })) || []),
      attendeesHash: payload.attendees?.sort().join(',') || '',
    },
  ],
  queryFn: () => {
    return getSelectedResource(payload);
  },
  // Cache strategy
  staleTime: 2 * 60 * 1000, // Cache fresh for 2 minutes
  gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
});

type GetSelectedResourceType = {
  queryConfig?: QueryConfig<typeof options>;
  payload: IGetResourceBookingRequest;
  enabled?: boolean;
};

/**
 * Hook to fetch selected resource data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useGetSelectedResource({
 *   payload: {
 *     requestId: "123",
 *     organizer: "user@example.com",
 *     timeZoneStandardName: "SE Asia Standard Time",
 *     attendees: ["attendee@example.com"],
 *     uid: "meeting-uid",
 *     globalId: "global-id",
 *     isNoEndDate: false,
 *     isRecurring: false,
 *     occurrences: [{
 *       utcStartDate: "2024-11-14T09:00:00Z",
 *       utcEndDate: "2024-11-14T10:00:00Z"
 *     }]
 *   },
 *   enabled: true // optional: control when query runs
 * });
 * 
 * // Access selected resource data
 * const resources = data?.resources || [];
 * ```
 */
export const useGetSelectedResource = ({ queryConfig, payload, enabled = true }: GetSelectedResourceType) => {
  const query = useQuery({
    ...options(payload),
    ...queryConfig,
    enabled: enabled,
  });
  return query;
};
